"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

interface TOCItem {
  id: string;
  title: string;
  level: number;
}

function useActiveItem(itemIds: string[]) {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (itemIds.length > 0 && !activeId) {
      setActiveId(itemIds[0] ?? null);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "0% 0% -80% 0%" },
    );

    for (const id of itemIds ?? []) {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    }

    return () => {
      for (const id of itemIds ?? []) {
        const element = document.getElementById(id);
        if (element) {
          observer.unobserve(element);
        }
      }
    };
  }, [itemIds, activeId]);

  return activeId;
}

export function LegalTableOfContents({
  headings,
  className,
}: {
  headings: TOCItem[];
  className?: string;
}) {
  const itemIds = React.useMemo(
    () => headings.map((item) => item.id),
    [headings],
  );

  const activeHeading = useActiveItem(itemIds);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 96;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  if (!headings?.length) {
    return null;
  }

  return (
    <div className={cn("space-y-1", className)}>
      <p className="text-muted-foreground mb-3 text-xs font-medium tracking-wider uppercase">
        Contents
      </p>
      <nav className="flex flex-col gap-1.5">
        {headings.map((item) => (
          <button
            key={item.id}
            onClick={() => {
              scrollToSection(item.id);
            }}
            className={cn(
              "cursor-pointer text-left text-sm leading-snug transition-colors",
              item.level === 3 && "pl-3",
              item.level === 4 && "pl-6",
              item.id === activeHeading
                ? "text-foreground font-medium"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.title}
          </button>
        ))}
      </nav>
    </div>
  );
}
